import FilterBar from '@/components/linear/filter-bar';
import IssueBoard from '@/components/linear/issue-board';
import IssueDetail from '@/components/linear/issue-detail';
import IssueList from '@/components/linear/issue-list';
import IssueTimeline from '@/components/linear/issue-timeline';
import PageToolbar from '@/components/linear/page-toolbar';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import Pagination from '@/components/pagination';
import {
  hasShowCompleted,
  issueQueryView,
  setIssueQueryView,
  setShowCompleted,
  stripViewTokens,
} from '@/lib/issue-query';
import { issuesPath, viewsPath } from '@/lib/linear-paths';
import { isTypingTarget, type IssueOrder } from '@/lib/linear-utils';
import { saveView } from '@/lib/views';
import { issues_path } from '@/rails/routes';
import type { Issue, IssueView } from '@/types/issue';
import type { IssueLabel } from '@/types/label';
import type { PaginationData } from '@/types/pagination';
import type { SharedPageProps } from '@/types/shared';
import { router, usePage } from '@inertiajs/react';
import { SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

interface Props {
  issues?: Issue[];
  view?: IssueView;
  q?: string;
  peekIssue?: Issue | null;
  pagination?: PaginationData;
}

export default function Index({ issues = [], view, q = '', peekIssue, pagination }: Props) {
  const { labels: workspaceLabels = [] } = usePage<SharedPageProps & { labels?: IssueLabel[] }>()
    .props;
  const url = usePage().url;
  const params = new URLSearchParams(url.split('?')[1] ?? '');
  const currentView = view ?? issueQueryView(q);
  const peekId = params.get('issue');
  const [draft, setDraft] = useState(() => stripViewTokens(q));
  const [displayOpen, setDisplayOpen] = useState(false);
  const {
    paletteOpen,
    createIssueOpen,
    layout,
    setLayout,
    grouping,
    setGrouping,
    order,
    setOrder,
  } = useWorkspaceChrome();
  const showCompleted = hasShowCompleted(q);

  useEffect(() => {
    setDraft(stripViewTokens(q));
  }, [q]);

  const labels = useMemo(() => {
    const map = new Map<string, IssueLabel>();
    workspaceLabels.forEach((label) => map.set(label.id, label));
    issues.forEach((issue) => {
      issue.labels?.forEach((label) => map.set(label.id, label));
    });
    return Array.from(map.values());
  }, [issues, workspaceLabels]);

  const visible = issues;

  const peek =
    peekIssue ??
    (peekId
      ? issues.find((issue) => issue.id === peekId || issue.identifier === peekId)
      : undefined);

  const visit = useCallback(
    (nextQ: string, issue?: string, nextOrder: IssueOrder = order) => {
      router.get(
        issuesPath(),
        {
          q: nextQ || undefined,
          issue,
          order: nextOrder,
        },
        { preserveState: true, preserveScroll: true, replace: true }
      );
    },
    [order]
  );

  const setView = (next: IssueView) => {
    visit(setIssueQueryView(q, next), peekId || undefined);
  };

  const openPeek = (issue: Issue) => {
    visit(q, issue.identifier);
  };

  const closePeek = useCallback(() => {
    visit(q, undefined);
  }, [q, visit]);

  const submitDraft = () => {
    visit(setIssueQueryView(draft, currentView), peekId || undefined);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (paletteOpen || createIssueOpen) return;
        if (peekId) closePeek();
        return;
      }
      if (isTypingTarget(event.target)) return;
      if (event.key.toLowerCase() === 'f' && !event.metaKey && !event.shiftKey) {
        event.preventDefault();
        const input = document.querySelector<HTMLInputElement>('.ln-filter-input');
        input?.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closePeek, createIssueOpen, paletteOpen, peekId]);

  return (
    <div className="ln-page">
      <PageToolbar
        tabs={
          <div className="ln-tabs">
            <Tab active={currentView === 'all'} onClick={() => setView('all')}>
              All issues
            </Tab>
            <Tab active={currentView === 'active'} onClick={() => setView('active')}>
              Active
            </Tab>
            <Tab active={currentView === 'backlog'} onClick={() => setView('backlog')}>
              Backlog
            </Tab>
          </div>
        }
        actions={
          <>
            <input
              className="ln-filter-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitDraft();
                }
              }}
              placeholder="Filter… assignee:me OR label:Bug -status:done"
              aria-label="Filter issues"
            />
            <FilterBar
              q={q}
              onChange={(next) => visit(next, peekId || undefined)}
              labels={labels}
            />
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="ln-toolbar-btn"
                onClick={() => setDisplayOpen((open) => !open)}
              >
                <SlidersHorizontal size={14} />
                Display
              </button>
              {displayOpen ? (
                <div className="ln-popover" style={{ top: 32, right: 0, minWidth: 240 }}>
                  <div className="ln-palette-group">Layout</div>
                  <button
                    type="button"
                    className="ln-popover-item"
                    data-active={layout === 'list' ? 'true' : 'false'}
                    onClick={() => setLayout('list')}
                  >
                    List
                  </button>
                  <button
                    type="button"
                    className="ln-popover-item"
                    data-active={layout === 'board' ? 'true' : 'false'}
                    onClick={() => setLayout('board')}
                  >
                    Board
                  </button>
                  <button
                    type="button"
                    className="ln-popover-item"
                    data-active={layout === 'timeline' ? 'true' : 'false'}
                    onClick={() => setLayout('timeline')}
                  >
                    Timeline
                  </button>
                  <div className="ln-palette-group">Grouping</div>
                  {(['status', 'assignee', 'priority', 'project', 'none'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="ln-popover-item"
                      data-active={grouping === value ? 'true' : 'false'}
                      onClick={() => setGrouping(value)}
                    >
                      {value === 'none' ? 'No grouping' : value[0].toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                  <div className="ln-palette-group">Ordering</div>
                  {(['updated', 'created', 'priority', 'title'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="ln-popover-item"
                      data-active={order === value ? 'true' : 'false'}
                      onClick={() => {
                        setOrder(value);
                        visit(q, peekId || undefined, value);
                      }}
                    >
                      {value[0].toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="ln-popover-item"
                    data-active={showCompleted ? 'true' : 'false'}
                    onClick={() => visit(setShowCompleted(q, !showCompleted), peekId || undefined)}
                  >
                    Show completed
                  </button>
                  <button
                    type="button"
                    className="ln-popover-item"
                    onClick={() => {
                      const name = window.prompt('View name');
                      if (!name?.trim()) return;
                      const href = window.location.pathname + window.location.search;
                      saveView(name.trim(), href);
                      router.post(
                        viewsPath(),
                        {
                          saved_view: {
                            name: name.trim(),
                            query: q,
                            layout,
                            grouping,
                          },
                        },
                        { preserveScroll: true }
                      );
                      setDisplayOpen(false);
                    }}
                  >
                    Save as view
                  </button>
                </div>
              ) : null}
            </div>
          </>
        }
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div className="ln-page-scroll" style={{ flex: 1 }}>
          {layout === 'board' ? (
            <IssueBoard
              issues={visible}
              selectedId={peekId}
              onSelect={openPeek}
              view={currentView}
              groupBy={grouping}
            />
          ) : layout === 'timeline' ? (
            <IssueTimeline issues={visible} selectedId={peekId} onSelect={openPeek} />
          ) : (
            <IssueList
              issues={visible}
              selectedId={peekId}
              onSelect={openPeek}
              groupBy={grouping}
            />
          )}
          {pagination ? (
            <Pagination
              pagination={pagination}
              pathBuilder={(params) => issues_path(params as { q?: string; page?: number })}
              queryParams={{ q: q || undefined, order }}
              preserveScroll
              preserveState
            />
          ) : null}
        </div>
        {peek ? (
          <div className="ln-peek">
            <IssueDetail key={peek.id} issue={peek} onClose={closePeek} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="ln-tab"
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
